import { useState, useEffect, useCallback } from 'react';
import { DeploymentStatus, WordPressConfig, LogEntry } from '../types/wordpress.type';
import { createDeploymentStages, eventTemplates } from '../constants/wordpressConfig';

export const useDeploymentSimulation = (config: WordPressConfig) => {
  const [status, setStatus] = useState<DeploymentStatus>({
    id: `wp-${Date.now()}`,
    config,
    status: 'deploying',
    currentStep: 0,
    totalSteps: 10,
    progress: 0,
    stages: createDeploymentStages(),
    events: [],
    startedAt: new Date(),
    estimatedTime: 104,
    health: 'updating',
    healthMessage: 'Deployment in progress',
  });

  const [elapsedTime, setElapsedTime] = useState(0);
  const [isCancelled, setIsCancelled] = useState(false);

  const addEvent = useCallback((severity: LogEntry['severity'], message: string, category: string) => {
    const newEvent: LogEntry = {
      id: `event-${Date.now()}-${Math.random()}`,
      timestamp: new Date(),
      severity,
      message,
      category,
    };

    setStatus(prev => ({
      ...prev,
      events: [newEvent, ...prev.events].slice(0, 50),
    }));
  }, []);

  const getRandomEvent = (type: keyof typeof eventTemplates): string => {
    const templates = eventTemplates[type];
    return templates[Math.floor(Math.random() * templates.length)];
  };

  const updateStage = useCallback((stageId: number, updates: any) => {
    setStatus(prev => ({
      ...prev,
      stages: prev.stages.map(stage =>
        stage.id === stageId ? { ...stage, ...updates } : stage
      ),
    }));
  }, []);

  const updateSubstep = useCallback((stageId: number, substepId: string, substepStatus: string) => {
    setStatus(prev => ({
      ...prev,
      stages: prev.stages.map(stage =>
        stage.id === stageId
          ? {
              ...stage,
              substeps: stage.substeps.map(substep =>
                substep.id === substepId ? { ...substep, status: substepStatus as any } : substep
              ),
            }
          : stage
      ),
    }));
  }, []);

  useEffect(() => {
    if (isCancelled) return;

    const stages = createDeploymentStages();
    let currentStageIndex = 0;
    let stageStartTime = 0;
    let substepIndex = 0;
    let substepTimer = 0;

    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1);

      const currentStage = stages[currentStageIndex];

      // Si ya completamos todos los stages
      if (!currentStage) {
        setStatus(prev => ({
          ...prev,
          status: 'completed',
          progress: 100,
          currentStep: 10,
          health: 'ok',
          healthMessage: 'All systems operational',
          completedAt: new Date(),
        }));
        addEvent('SUCCESS', '✅ WordPress deployment completed successfully!', 'deployment');
        addEvent('SUCCESS', `🎉 Your site is now live at https://${config.domain}`, 'deployment');
        clearInterval(interval);
        return;
      }

      // Iniciar nuevo stage
      if (stageStartTime === 0) {
        stageStartTime = Date.now();
        substepIndex = 0;
        substepTimer = 0;

        updateStage(currentStage.id, {
          status: 'in-progress',
          startedAt: new Date(),
        });

        addEvent('INFO', `🚀 Starting: ${currentStage.name}`, 'deployment');
      }

      // Calcular tiempo transcurrido en el stage actual
      const stageElapsed = Math.floor((Date.now() - stageStartTime) / 1000);
      const stageDuration = currentStage.duration || 0;

      // Procesar substeps progresivamente
      if (currentStage.substeps.length > 0) {
        const substepsPerSecond = stageDuration / currentStage.substeps.length;
        
        if (substepIndex < currentStage.substeps.length) {
          substepTimer += 1;
          
          if (substepTimer >= substepsPerSecond || substepIndex === 0) {
            const substep = currentStage.substeps[substepIndex];
            
            if (substep) {
              // Marcar substep como in-progress primero
              updateSubstep(currentStage.id, substep.id, 'in-progress');
              
              // Después de un breve momento, marcarlo como completed
              setTimeout(() => {
                updateSubstep(currentStage.id, substep.id, 'completed');
                addEvent('INFO', `  ✓ ${substep.name}`, currentStage.name.toLowerCase());
              }, 300);
              
              substepIndex++;
              substepTimer = 0;
            }
          }
        }
      }

      // Calcular progreso total
      const totalDuration = stages.reduce((sum, s) => sum + (s.duration || 0), 0);
      const completedDuration = stages
        .slice(0, currentStageIndex)
        .reduce((sum, s) => sum + (s.duration || 0), 0);
      const currentProgress = ((completedDuration + stageElapsed) / totalDuration) * 100;

      setStatus(prev => ({
        ...prev,
        progress: Math.min(Math.round(currentProgress), 99),
        currentStep: currentStageIndex + 1,
      }));

      // Eventos aleatorios de warnings (5% de probabilidad)
      if (Math.random() < 0.05) {
        addEvent('WARN', `⚠ ${getRandomEvent('warn')}`, 'monitoring');
      }

      // Eventos aleatorios de info (15% de probabilidad)
      if (Math.random() < 0.15) {
        addEvent('INFO', getRandomEvent('info'), 'system');
      }

      // Completar stage cuando se acabe el tiempo
      if (stageElapsed >= stageDuration) {
        updateStage(currentStage.id, {
          status: 'completed',
          completedAt: new Date(),
          duration: stageElapsed,
        });
        
        addEvent('SUCCESS', `✅ Completed: ${currentStage.name} (${stageElapsed}s)`, 'deployment');
        
        // Agregar evento de éxito específico por stage
        const successEvent = eventTemplates.success[currentStageIndex % eventTemplates.success.length];
        if (successEvent) {
          addEvent('SUCCESS', successEvent, 'deployment');
        }
        
        currentStageIndex++;
        stageStartTime = 0;
        substepIndex = 0;
        substepTimer = 0;
      }
    }, 1000);

    // Eventos iniciales
    addEvent('INFO', '🎬 Deployment process initiated', 'deployment');
    addEvent('INFO', `📦 Creating WordPress site: ${config.name}`, 'deployment');
    addEvent('INFO', `🌐 Domain: ${config.domain}`, 'deployment');
    addEvent('INFO', `💻 Instance type: ${config.instanceType}`, 'deployment');
    addEvent('INFO', `🔧 PHP ${config.phpVersion} | WordPress ${config.wpVersion}`, 'deployment');
    
    if (config.enableCDN) {
      addEvent('INFO', '🚀 CDN will be configured', 'deployment');
    }
    
    if (config.sslEnabled) {
      addEvent('INFO', '🔒 SSL certificate will be provisioned', 'deployment');
    }

    return () => clearInterval(interval);
  }, [isCancelled, config, addEvent, updateStage, updateSubstep]);

  const cancelDeployment = () => {
    setIsCancelled(true);
    setStatus(prev => ({
      ...prev,
      status: 'failed',
      health: 'degraded',
      healthMessage: 'Deployment cancelled by user',
    }));
    addEvent('ERROR', '❌ Deployment cancelled by user', 'deployment');
  };

  return { status, elapsedTime, cancelDeployment };
};